import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List, type RowComponentProps } from 'react-window';
import { FilterDropdownButton } from './FilterDropdownButton';

export type MultiSelectFilterOption = {
  value: string;
  label: string;
};

export interface MultiSelectFilterDropdownProps {
  label: string;
  options: MultiSelectFilterOption[];
  selectedValues: string[];
  onApply: (values: string[]) => void;
  loading?: boolean;
  controlHeight?: number;
  searchPlaceholder?: string;
  showSearch?: boolean;
  renderOption?: (option: MultiSelectFilterOption, isChecked: boolean) => ReactNode;
  getOptionHeight?: (option: MultiSelectFilterOption) => number;
  dataTest?: string;
  isActive?: boolean;
  minWidth?: number;
}

const DEFAULT_ITEM_SIZE = 40;
const MAX_VISIBLE_ITEMS = 6;

const areStringSetsEqual = (firstValues: string[], secondValues: string[]) => {
  if (firstValues.length !== secondValues.length) {
    return false;
  }

  const secondValuesSet = new Set(secondValues);

  return firstValues.every(value => secondValuesSet.has(value));
};

type FastFiltersRowProps = {
  filteredOptions: MultiSelectFilterOption[];
  draftValues: string[];
  toggleValue: (value: string) => void;
  renderOption?: (option: MultiSelectFilterOption, isChecked: boolean) => ReactNode;
};

const getRowKey = (index: number, { filteredOptions }: FastFiltersRowProps) => filteredOptions[index].value;

function FastFiltersRow({
  index,
  style,
  filteredOptions,
  draftValues,
  toggleValue,
  renderOption,
}: RowComponentProps<FastFiltersRowProps>): ReactElement {
  const option = filteredOptions[index];
  const isChecked = draftValues.includes(option.value);

  return (
    <ListItemButton
      dense
      onClick={() => toggleValue(option.value)}
      style={style}
      sx={{
        alignItems: 'center',
        px: 0.75,
        py: 0.75,
        boxSizing: 'border-box',
        backgroundColor: theme => (isChecked ? theme.palette.primary.light : 'transparent'),
        borderTop: theme => `2px solid ${isChecked ? theme.palette.background.paper : 'transparent'}`,
        borderBottom: theme => `2px solid ${isChecked ? theme.palette.background.paper : 'transparent'}`,
        '&:hover': {
          backgroundColor: isChecked ? 'primary.light' : 'action.hover',
          borderTopColor: 'background.paper',
          borderBottomColor: 'background.paper',
        },
      }}
    >
      <Checkbox checked={isChecked} size="small" />
      <ListItemText
        primary={renderOption ? renderOption(option, isChecked) : option.label}
        sx={{ my: 0 }}
        slotProps={{
          primary: {
            sx: {
              lineHeight: 1.25,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            },
          },
        }}
      />
    </ListItemButton>
  );
}

export const MultiSelectFilterDropdown = ({
  label,
  options,
  selectedValues,
  onApply,
  loading = false,
  controlHeight = 40,
  searchPlaceholder,
  showSearch = false,
  renderOption,
  getOptionHeight,
  dataTest,
  isActive = false,
  minWidth = 170,
}: MultiSelectFilterDropdownProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [draftValues, setDraftValues] = useState<string[]>(selectedValues);

  const open = Boolean(anchorEl);
  const hasDraftChanges = !areStringSetsEqual(draftValues, selectedValues);
  const isApplyDisabled = !hasDraftChanges;
  const isClearDisabled = draftValues.length === 0 && selectedValues.length === 0;

  useEffect(() => {
    if (!open) {
      setDraftValues(selectedValues);
      setSearchPhrase('');
    }
  }, [open, selectedValues]);

  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchPhrase.trim()) return options;
    const phrase = searchPhrase.toLowerCase();

    return options.filter(option => option.label.toLowerCase().includes(phrase));
  }, [options, searchPhrase, showSearch]);

  const itemHeights = useMemo(() => {
    return filteredOptions.map(option => getOptionHeight?.(option) ?? DEFAULT_ITEM_SIZE);
  }, [filteredOptions, getOptionHeight]);

  const listHeight = useMemo(() => {
    const maxVisibleHeight = itemHeights.slice(0, MAX_VISIBLE_ITEMS).reduce((acc, height) => acc + height, 0);
    return maxVisibleHeight || DEFAULT_ITEM_SIZE;
  }, [itemHeights]);

  const toggleValue = useCallback((value: string) => {
    setDraftValues(prev => (prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]));
  }, []);

  const getRowHeight = useCallback((index: number) => itemHeights[index] ?? DEFAULT_ITEM_SIZE, [itemHeights]);

  const handleApply = () => {
    onApply(draftValues);
    setAnchorEl(null);
  };

  const handleClear = () => {
    setDraftValues([]);
  };

  return (
    <>
      <FilterDropdownButton
        label={label}
        open={open}
        onClick={event => setAnchorEl(event.currentTarget)}
        activeCount={selectedValues.length}
        controlHeight={controlHeight}
        dataTest={dataTest}
        isActive={isActive}
        minWidth={minWidth}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper sx={{ width: 320 }}>
          {showSearch && (
            <Box sx={{ px: 0.75, py: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={searchPlaceholder ?? t('Search')}
                value={searchPhrase}
                onChange={event => setSearchPhrase(event.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List
              key={`${label}-${filteredOptions.length}-${searchPhrase}`}
              rowComponent={FastFiltersRow}
              rowCount={filteredOptions.length}
              rowHeight={getRowHeight}
              rowKey={getRowKey}
              rowProps={{
                filteredOptions,
                draftValues,
                toggleValue,
                renderOption,
              }}
              overscanCount={6}
              style={{ height: listHeight, width: 320 }}
            />
          )}

          <Divider />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', px: 0.75, py: 1 }}>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={isApplyDisabled}
              data-test="fast-filters-apply-button"
            >
              {t('Apply')}
            </Button>
            <Button
              variant="text"
              onClick={handleClear}
              disabled={isClearDisabled}
              sx={{ color: isClearDisabled ? undefined : 'text.secondary' }}
            >
              {t('Clear Filters')}
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};
